/* Selector file rule: root-level functions in this file must be exported selectors only. Move helper functions to lib files. */
import { createSelector } from 'reselect';
import { NON_RUNNING_RUN_STATES } from '@/lib/pieveRunState.js';
import { matchesScheduleId } from '@/lib/automationSchedules/scheduleState.js';
import { threadContentMap, threadMetaById } from '@/redux/selectors/pieveSelectors/baseSelectors.js';
import { resolvedComposerSandboxMode } from '@/redux/selectors/pieveSelectors/composerResolutionSelectors.js';
import { automationScheduleItems } from '@/redux/selectors/pieveSelectors/automationSharedSelectors.js';
import getAutomationComposerModelId from '@/redux/selectors/pieveSelectors/lib/getAutomationComposerModelId.js';
import getAutomationComposerReasoningEffort from '@/redux/selectors/pieveSelectors/lib/getAutomationComposerReasoningEffort.js';
import getAutomationItemThreadId from '@/redux/selectors/pieveSelectors/lib/getAutomationItemThreadId.js';
import getAutomationRouteRequestedId from '@/redux/selectors/pieveSelectors/lib/getAutomationRouteRequestedId.js';
import getCurrentAutomationRequestedId from '@/redux/selectors/pieveSelectors/lib/getCurrentAutomationRequestedId.js';
import getCurrentAutomationRunThreadId from '@/redux/selectors/pieveSelectors/lib/getCurrentAutomationRunThreadId.js';
import getThreadContentValue from '@/redux/selectors/pieveSelectors/lib/getThreadContentValue.js';
import getThreadMetaValue from '@/redux/selectors/pieveSelectors/lib/getThreadMetaValue.js';
import { activeThreadId } from '@/redux/selectors/pieveSelectors/activeThreadIdSelector.js';
import { threadActiveRunId, threadRunState } from '@/redux/selectors/pieveSelectors/threadRunSelectors.js';
import { automationScheduleRunSelectionContext as selectedAutomationScheduleRunSelectionContext, automationScheduleSelectionId as selectedAutomationScheduleSelectionId, pathname as selectedPathname, query as selectedQuery } from '@/redux/selectors/uiSelectors.js';

export const currentAutomationScheduleRequestedId = createSelector([selectedPathname, activeThreadId, threadMetaById, selectedAutomationScheduleSelectionId], (pathname, activeThreadId, threadMetaByIdValue, selectedScheduleId) => {
  return getCurrentAutomationRequestedId({
    routeRequestedId: getAutomationRouteRequestedId({
      pathname,
      automationType: 'schedules'
    }),
    activeThreadId,
    threadMetaByIdValue,
    ownerThreadType: 'schedule',
    runThreadType: 'schedule_run',
    selectedRequestedId: selectedScheduleId
  });
});

export const currentAutomationSchedule = createSelector([automationScheduleItems, currentAutomationScheduleRequestedId], (scheduleItems, requestedId) => {
  if (!requestedId) {
    return null;
  }

  return (Array.isArray(scheduleItems) ? scheduleItems.find((schedule) => matchesScheduleId(schedule, requestedId)) : null) || null;
});

export const currentAutomationScheduleId = createSelector([currentAutomationSchedule], (schedule) => {
  return String(schedule?.id || '').trim() || null;
});

export const currentAutomationScheduleConfig = createSelector([currentAutomationSchedule], (schedule) => {
  if (!schedule) {
    return null;
  }

  return {
    id: schedule.id || null,
    title: schedule.title || 'Untitled schedule',
    prompt: schedule.prompt || '',
    promptSummary: schedule.promptSummary || null,
    cadenceSummary: schedule.cadenceSummary || 'No cadence configured',
    nextRunSummary: schedule.nextRunSummary || null,
    active: Boolean(schedule.active),
    scheduleType: schedule.scheduleType || null,
    interval: schedule.interval || null,
    runTimes: Array.isArray(schedule.runTimes) ? schedule.runTimes : [],
    days: Array.isArray(schedule.days) ? schedule.days : [],
    timeZone: schedule.timeZone || null,
    actionSummary: schedule.actionSummary || null
  };
});

export const currentAutomationScheduleComposerModel = createSelector([currentAutomationSchedule, threadMetaById], (schedule, threadMetaByIdValue) => {
  return getAutomationComposerModelId({
    item: schedule,
    threadMetaByIdValue,
    fallbackThreadIdToItemId: true
  });
});

export const currentAutomationScheduleComposerReasoningEffort = createSelector([currentAutomationSchedule, threadMetaById, currentAutomationScheduleComposerModel], (schedule, threadMetaByIdValue, modelId) => {
  return getAutomationComposerReasoningEffort({
    item: schedule,
    threadMetaByIdValue,
    modelId,
    fallbackThreadIdToItemId: true
  });
});

export const currentAutomationScheduleRunThreadId = createSelector([selectedPathname, (state) => selectedQuery(state, 'run'), currentAutomationScheduleId, selectedAutomationScheduleRunSelectionContext], (pathname, routeRunThreadId, currentScheduleId, selectionContext) => {
  return getCurrentAutomationRunThreadId({
    pathname,
    routeRunThreadId,
    currentOwnerId: currentScheduleId,
    selectionContext,
    automationType: 'schedules',
    selectionOwnerKey: 'scheduleId'
  });
});

export const currentAutomationScheduleThreadId = createSelector([currentAutomationScheduleRunThreadId, currentAutomationSchedule], (runThreadId, schedule) => {
  return runThreadId || getAutomationItemThreadId({ item: schedule, fallbackToId: true });
});

export const currentAutomationScheduleThreadContent = createSelector([threadContentMap, currentAutomationScheduleThreadId], (threadContentMapValue, threadId) => {
  return getThreadContentValue({
    threadContentMapValue,
    threadId
  });
});

export const currentAutomationScheduleThreadMeta = createSelector([threadMetaById, currentAutomationScheduleThreadId], (threadMetaByIdValue, threadId) => {
  return getThreadMetaValue({
    threadMetaByIdValue,
    threadId
  });
});

export const currentAutomationScheduleRunState = (state) => threadRunState(state, currentAutomationScheduleThreadId(state));

export const currentAutomationScheduleActiveRunId = (state) => threadActiveRunId(state, currentAutomationScheduleThreadId(state));

export const currentAutomationScheduleIsRunning = createSelector([currentAutomationScheduleRunState], (runState) => {
  return !NON_RUNNING_RUN_STATES.has(runState);
});

export const currentAutomationScheduleSandboxMode = (state) => {
  return resolvedComposerSandboxMode(state, {
    threadId: currentAutomationScheduleThreadId(state) || ''
  });
};
